import React, { useState, useEffect } from 'react';
import {useForm} from "react-hook-form";
import { useAuthToken, useRequestUrl } from "../context/contextHooks";
import API from '../http/API';
import {useSxStyles as classes} from "../styles/sxStyles";
import { updateEntry, validateLogEntry } from "./callLoggingFunctions";
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid2 from '@mui/material/Grid2';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Select from "@mui/material/Select";

const EnterResult = (props) => {
    const logEntry = props.logEntry;
    const setLogEntry = props.setLogEntry;
    const currentUser = props.currentUser;
    const setNewCall = props.setNewCall;
    const clearData = props.clearData;
    const {
        register,
        handleSubmit
    } = useForm();
    const [requestUrl,] = useRequestUrl();
    const [authToken,] = useAuthToken();
    const [errors, setErrors] = useState(null);
    const [loading, setLoading] = useState(false);
    const [actionResponse, setActionResponse] = useState(null);
    const [entry, setEntry] = useState(logEntry);
    const [reasons, setReasons] = useState(null);
    const [chipReasons, setChipReasons] = useState(null);
    const [dropdownReasons, setDropdownReasons] = useState(null);

    const ReasonChip = (props) => {
        const labelText = props.labelText;

        return (
            <Chip
                label={labelText}
                color={entry && entry.outcomeReason === labelText ? 'success' : 'default'}
                onClick={() => handleReason(labelText)}
            />
        );
    }

    const clearMessages = () => {
        setErrors(null);
        setActionResponse(null);
    }

    const retrieveReasons = () => {
        clearMessages();
        setLoading(true);
        const url = `${requestUrl}/getOutcomeReasons/${currentUser.agencyId}`;
        API.getRetrieve(url, 'reasons', `Bearer ${authToken}`, setActionResponse);
    }

    const handleOutcome = (value) => {
        setEntry({...entry, outcome: value});
    }

    const handleReason = (value) => {
        setEntry({...entry, outcomeReason: value});
    }

    const saveEntry = (form) => {
        const toSave = updateEntry(entry, form);
        const msg = validateLogEntry(toSave);
        if (msg) {
            setErrors(msg);
        } else {
            clearMessages();
            setLoading(true);
            const url = `${requestUrl}/saveFinishedLogEntry`;
            API.postRetrieve(url, JSON.stringify(toSave), 'saveEntry', `Bearer ${authToken}`, setActionResponse);
        }
    }

    const handleNewCall = () => {
        setLogEntry(null);
        if (clearData) clearData();
        setNewCall(false);
    }

    useEffect(() => {
        if (actionResponse && loading) {
            if (actionResponse.error) {
                setErrors(actionResponse.errorMessage);
            } else if (actionResponse.requestType === 'reasons') {
                setReasons(actionResponse.data);
            } else if (actionResponse.requestType === 'saveEntry') {
                handleNewCall();
            }
            setLoading(false);
        }
    }, [actionResponse]);

    useEffect(() => {
        if (!reasons) {
            retrieveReasons();
        } else if (reasons.length > 0) {
            const chipz = reasons.filter(r => r.chip);
            const dropz = reasons.filter(r => !r.chip);
            if (chipz && chipz.length > 0) setChipReasons(chipz);
            if (dropz && dropz.length > 0) setDropdownReasons(dropz);
        }
    }, [reasons])

    return (<>
        <Paper elevation={3} sx={classes.controlPanel}>
            <Typography sx={classes.inputLabel} variant="h5" color='primary'>Enter Call Outcome</Typography>
            { errors &&
                <Alert severity='error'>
                    <AlertTitle>Error!</AlertTitle>
                    {errors}
                </Alert>
            }
            <Grid2 container spacing={1} alignItems='top'>
                <Grid2
                    size={{
                        xs: 12,
                        md: 6
                    }}>
                    <FormControl
                        fullWidth
                        required
                        {...register('callerFirstName')}
                    >
                        <FormLabel shrink={true} sx={classes.inputLabel}>First Name</FormLabel>
                        <TextField
                            id="callerFirstName"
                            name="callerFirstName"
                            variant='outlined'
                            autoComplete="off"
                            fullWidth
                            defaultValue={entry ? entry.callerFirstName : ''}
                            slotProps={{
                                htmlInput: {maxLength: 100, sx: classes.textbox}
                            }}
                        />
                    </FormControl>
                </Grid2>
                <Grid2
                    size={{
                        xs: 12,
                        md: 6
                    }}>
                    <FormControl
                        fullWidth
                        required
                        {...register('callerLastName')}
                    >
                        <FormLabel shrink={true} sx={classes.inputLabel}>Last Name</FormLabel>
                        <TextField
                            id="callerLastName"
                            name="callerLastName"
                            variant='outlined'
                            autoComplete="off"
                            fullWidth
                            defaultValue={entry ? entry.callerLastName : ''}
                            slotProps={{
                                htmlInput: {maxLength: 100, sx:classes.textbox}
                            }}
                        />
                    </FormControl>
                </Grid2>
                <Grid2 size={12} >
                    <Stack direction='row' spacing={1} useFlexGap flexWrap="wrap">
                        <FormLabel shrink={true} sx={classes.inputLabel}>Outcome:</FormLabel>
                        <Chip
                            label='Referred to Court'
                            color={entry && entry.outcome === 'Referred to Court' ? 'success' : 'default'}
                            onClick={() => handleOutcome('Referred to Court')}
                        />
                        { entry && entry.paymentDeclined &&
                            <Chip
                                label='Payment Declined'
                                color={entry && entry.outcome === 'Payment Declined' ? 'success' : 'default'}
                                onClick={() => handleOutcome('Payment Declined')}
                            />
                        }
                        <Chip
                            label='N/A'
                            color={entry && entry.outcome === 'N/A' ? 'success' : 'default'}
                            onClick={() => handleOutcome('N/A')}
                        />
                    </Stack>
                </Grid2>
                { reasons && reasons.length > 0 &&
                    < Grid2 size={12} >
                        <Stack direction='row' spacing={1} useFlexGap flexWrap="wrap">
                            <FormLabel shrink={true} sx={classes.inputLabel}>Reason:</FormLabel>
                            { chipReasons && chipReasons.map((reason) => {
                                return (
                                    <ReasonChip labelText={reason.reasonDescription}/>
                                );
                            })}
                        </Stack>
                        { dropdownReasons &&
                            <FormControl
                                fullWidth
                                required
                                sx={{marginTop: '.5rem'}}
                            >
                                <Select
                                    name='court'
                                    native variant='outlined'
                                    fullWidth
                                    onChange={(e) => handleReason(e.target.value)}
                                    sx={{height: '2rem'}}
                                >
                                    <option key='none' value=''>None Selected</option>
                                    {dropdownReasons.map((reason) => (
                                        <option key={reason.id} value={reason.reasonDescription}>{reason.reasonDescription}</option>
                                    ))}
                                </Select>
                            </FormControl>
                        }
                    </Grid2>
                }
                < Grid2 size={12} >
                    <FormControl
                        fullWidth
                        {...register('comments')}
                    >
                        <FormLabel shrink={true} sx={classes.inputLabel}>Comments</FormLabel>
                        <TextField
                            id='comments'
                            name='comments'
                            fullWidth
                            multiline
                            defaultValue={entry && entry.comments ? entry.comments : ''}
                            inputComponent='textarea'
                            slotProps={{
                                htmlInput: {
                                    rows: 4,
                                    style: {height: '5vh', resize: 'none'}
                                }
                            }}
                        />
                    </FormControl>
                </Grid2>
                < Grid2 size={12}>
                    <Box component='div' sx={{...classes.root, display: 'flex', flexDirection: 'row-reverse', margin: '1rem 3rem'}} >
                        <Button
                            variant='contained'
                            color='primary'
                            sx={classes.squashedButton}
                            onClick={handleSubmit(saveEntry)}
                        >
                            Enter Call
                        </Button>
                        <Button
                            variant='outlined'
                            color='primary'
                            sx={classes.squashedButton}
                            onClick={() => setNewCall(false)}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Grid2>
            </Grid2>
        </Paper>
    </>);
}

export default EnterResult;
